import React, {
  useState, useCallback,
  useMemo, useEffect
} from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import { FaDownload } from 'react-icons/fa';
import { Dropdown, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { IoIosOptions } from 'react-icons/io';
import { selectors, hooks } from 'farmx-redux-core';
import { getDownloadUrl } from 'helper/graphHelper';
import NewChartComponent from './NewChartComponent';
import Resizer from './Resizer';
import { useSensorGraphData, usePrepareGraphConfig, isValidRange } from './graphHooks';
import { getSensorTypeShortForm, NO_RENDER } from './constants';
import { updateChartHeight } from 'helper/common';

const {
  selectSensor, selectSensorById
} = selectors;

const { useSensor } = hooks;

function showText(t, text, showLoading){
  return (
    <div className='chart-loading-container'>
      <div className="chart-loading">
        {showLoading && <span className="chart-loading-inner"><LoadingOutlined /></span>}
        <span className="chart-loading-inner">{`${t(text)}`}</span>
      </div>
    </div>
  );
}

export default function NewSensorDataChart(props) {
  const {
    sensorName, sensorType, sensorId, sensorIdentifier,
    variables, startDate, endDate, cached, customConfig,
    header, uniformYAxis, yMinMax, renderOption, preparedConfig,
    refresh, sensorsString, isMenuChange
  } = props;
  const [chartRef, setChartRef] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const { t } = useTranslation();

  const sensorsByPropValues = useSelector((state) => selectSensor(state,
    sensorType, sensorIdentifier));

  // Use sensorId from props, or fallback to id from Redux state
  // This ensures we have a valid ID even when not passed as prop
  const effectiveSensorId = sensorId || sensorsByPropValues?.id;

  const sensor = useSensor({ type: sensorType, id: effectiveSensorId, identifier: sensorIdentifier });
  const { node } = sensor || {};
  const nodeSensor = useSelector((state) => selectSensorById(state, 'cavalier', node));

  // Auto-refresh effect - runs when auto-refresh is enabled
  useEffect(() => {
    if (autoRefreshEnabled) {
      const intervalId = setInterval(() => {
        setRefreshCounter(prev => prev + 1);
      }, 60000); // 60000ms = 1 minute

      return () => clearInterval(intervalId);
    }
  }, [autoRefreshEnabled]);

  // TODO remove after testing
  // const graphRef = useRef();
  // console.log('calling NewSensorDataChart', preparedConfig);
  // console.log(startDate, endDate);

  // Combine external refresh prop with internal refresh counter
  const combinedRefresh = (typeof refresh === 'number') ? refresh + refreshCounter : refreshCounter;

  const { loading, data, error } = useSensorGraphData(
    sensorType,
    effectiveSensorId,
    sensorIdentifier,
    variables,
    startDate,
    endDate,
    cached,
    combinedRefresh,
    preparedConfig,
    renderOption,
    sensorsString
  );

  const config = usePrepareGraphConfig(
    customConfig,
    data,
    startDate,
    endDate,
    uniformYAxis,
    yMinMax,
    chartRef,
    preparedConfig,
    renderOption,
    sensorType,
    sensorIdentifier,
  );

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('config changed', config, renderOption);
  // }, [config, renderOption]);

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('graphRef changed', graphRef.current);
  // }, []);

  const getNodeName = useCallback(() => {
    if(nodeSensor?.identifier) {
      return `[${t("node")}: ${(nodeSensor.identifier)?.slice(-4)}]`;
    }

    return '';
  }, [nodeSensor]);

  const onResize = useCallback((event, chartHeight, wrapperElem) => {
    updateChartHeight(chartHeight, wrapperElem);
  }, []);

  const getConfig = useCallback(() => {
    if(uniformYAxis && yMinMax){
      // TODO remove after testing
      // console.log('updating yaxis extremes', yMinMax);
      const { min, max} = yMinMax;
      if (isValidRange(min) && isValidRange(max)) {
        return {...config, yAxis: config?.yAxis?.map((d) => ({...d, min, max})) };
      }
      return config;
    }
    return config;
  }, [config, uniformYAxis, yMinMax]);

  const getSensorName = useCallback(() => {
    if(sensorName) return sensorName;
    if(sensorsByPropValues?.name) return sensorsByPropValues.name;
    return `${getSensorTypeShortForm(sensorType)}-${sensorIdentifier}`;
  }, [sensorIdentifier, sensorName, sensorType, sensorsByPropValues]);

  const downloadCSV = useCallback((e) => {
    getDownloadUrl(
      startDate,
      endDate,
      {type: sensorType, identifier: sensorIdentifier},
      variables,
      () => null
    );
  },
  [startDate, endDate, sensorType, sensorIdentifier, variables]);

  const isLoading = (loading || (preparedConfig===null));
  const noData = (preparedConfig && !preparedConfig?.series) ||
  (config &&
    (
      !config.series?.length ||
      config?.series?.every(series => !series.data?.length)
    )
  );

  const headerRightExtras = !isLoading
    && !noData
    && header?.right?.map((d, i) => ({label: d, key: i+2}));

  const toggleAutoRefresh = useCallback((e) => {
    e.stopPropagation();
    setAutoRefreshEnabled(prev => !prev);
  }, []);

  const items = useMemo(() => {
    const download = {
      label: (
        <div
          key="download"
          role="button"
          onClick={downloadCSV}
          onKeyDown={downloadCSV}
          tabIndex={0}
          style={{alignContent: 'center'}}
        >
          <FaDownload />
          <span style={{ marginLeft: '10px' }}>
            {t('Download')}
          </span>
        </div>

      ),
      key: 0
    };

    const autoRefresh = {
      label: (
        <div
          key="auto-refresh"
          role="button"
          onClick={toggleAutoRefresh}
          onKeyDown={toggleAutoRefresh}
          tabIndex={0}
          style={{alignContent: 'center'}}
        >
          <Checkbox
            checked={autoRefreshEnabled}
            onChange={toggleAutoRefresh}
            onClick={(e) => e.stopPropagation()}
          >
            {t('Auto-refresh')}
          </Checkbox>
        </div>
      ),
      key: 1
    };

    if(headerRightExtras?.length) return [download, autoRefresh, ...headerRightExtras];
    return [download, autoRefresh];
  }, [downloadCSV, headerRightExtras, autoRefreshEnabled, toggleAutoRefresh, t]);

  function handleChartRef(ref) {
    setChartRef(ref);

    // Store the chart ref by key
    const { actualChartKey, actualIdentifier } = ref?.current?.chart?.options?.series?.[0] || {};
    if (!window?.allCharts) {
      window.allCharts = { [`${actualChartKey}/${actualIdentifier}`]: ref?.current?.chart }
    } else if (window?.allCharts) {
      window.allCharts = { ...window.allCharts, [`${actualChartKey}/${actualIdentifier}`]: ref?.current?.chart }
    }
  }

  if(renderOption === NO_RENDER) return null;

  return (
    <div className="chart-component-wrapper">
      {header
        ?(
          <div className="chart-header-container">
            <div className="chart-header-form">
              <div className="chart-header-left">
                <div className="chart-sensor">
                  {getSensorName()}
                  <span
                    className="chart-title"
                  >
                    {header?.title}
                    {header?.units?` (${header.units})`:''}
                  </span>
                  <span className="chart-title">
                    {getNodeName()}
                  </span>
                </div>
                <div className="chart-header-legend">
                  {!isLoading && !noData
                    && header?.left?.map((d) => ((typeof d==='function')?d(config?.series):d))}
                </div>
              </div>
              <div className="chart-header-right">
                {!isLoading && !noData && header?.rightBefore?.map((item) => item)}
                <div id="chart-header-options" className="chart-header-options">
                  <Dropdown
                    disabled={isLoading}
                    menu={{ items}}
                    trigger={['click']}
                    getPopupContainer={trigger => trigger.parentNode}
                    >
                    <a>
                      <IoIosOptions size={20} color="grey" />
                    </a>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
        ):null}
        <Resizer onResize={onResize}>
        <div className="chart-component-main">
            { error && showText(t, error.message) }
            { (isLoading || isMenuChange) && showText(t, 'Loading...', true) }
            { !isLoading && noData && !isMenuChange && showText(t, 'No Data') }
            { !isLoading && !noData && !error
              && (
              <NewChartComponent
                config={getConfig()}
                error={null}
                setChartRef={handleChartRef}
              />
            )}
          </div>
        </Resizer>
    </div>
  );
}

NewSensorDataChart.propTypes = {
  sensorName: PropTypes.string,
  sensorType: PropTypes.string,
  sensorId: PropTypes.number,
  sensorIdentifier: PropTypes.string,
  variables: PropTypes.arrayOf(PropTypes.any),
  startDate: PropTypes.shape({}),
  endDate: PropTypes.shape({}),
  cached: PropTypes.string | null | undefined,
  customConfig: PropTypes.func,
  header: PropTypes.shape({
    title: PropTypes.string,
    units: PropTypes.string,
    left: PropTypes.arrayOf(PropTypes.any),
    right: PropTypes.arrayOf(PropTypes.any),
    rightBefore: PropTypes.arrayOf(PropTypes.any),
  }),
  uniformYAxis: PropTypes.bool,
  yMinMax: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number
  }),
  renderOption: PropTypes.string,
  preparedConfig: PropTypes.shape({
    series: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  refresh: PropTypes.number,
  sensorsString: PropTypes.string,
  isMenuChange: PropTypes.bool,
};
NewSensorDataChart.defaultProps = {
  sensorName: null,
  sensorType: null,
  sensorId: null,
  sensorIdentifier: null,
  variables: null,
  startDate: null,
  endDate: null,
  cached: null,
  customConfig: null,
  header: null,
  uniformYAxis: false,
  yMinMax: null,
  renderOption: null,
  preparedConfig: undefined,
  refresh: null,
  sensorsString: undefined,
  isMenuChange: false
};
